import Modal from '@components/common/modal'
import Spinner from '@components/common/spinner'
import {
  ClassRescheduleOptionsEnum,
  RescheduleModalStatesEnum,
} from '@lib/constants/reschedule-class'
import { useEffect, useMemo, useState } from 'react'
import Selection from '@components/reschedule-course-class/selection'
import ConfirmNextClassTime from '@components/reschedule-course-class/confirm-next-class-time'
import RescheduleSuccess from '@components/reschedule-course-class/reschedule-success'
import { ClassDetailsProps, StudentProps } from '@lib/types/classroom'
import NoSlotsAvailable from '@components/reschedule-course-class/no-slots-available'
import ContactRm from '@components/reschedule-course-class/contact-rm'
import { CClient, showErrorAlert } from '@common/utils'
import { useSessionContext } from '@contexts/session'
import { captureException } from '@sentry/node'
import date, { SYSTEM_TIMEZONE, getNextClassDate } from '@lib/utils/date.utils'
import AvailableSlots from '@components/trial-class/available-slots'
import Router from 'next/router'
import {
  flattenErrorsDictFromResponse,
  pollCeleryTaskStatus,
} from '@lib/utils/common'
import Polling from '@components/reschedule-course-class/polling'
import { handleReschdeuleAtSelectedTime } from '@lib/utils/reschedule'
import { RelationshipManagerType } from '@lib/types/common'
import CheckIfStudentIsInformed from '@components/reschedule-course-class/check-if-student-is-informed'
import RequestTeacherToInformStudent from '@components/reschedule-course-class/request-teacher-to-inform-student'
import { ClassScheduleType } from '@lib/types/reschedule'
import { X } from 'react-feather'
import { Dayjs } from 'dayjs'
import dynamic from 'next/dynamic'

const SelectRescheduleDateTime = dynamic(
  () =>
    import('@components/reschedule-course-class/select-reschedule-datetime'),
  {
    loading: () => (
      <div className="flex items-center justify-center h-40">
        <Spinner classes="text-orange h-10 w-10" />
      </div>
    ),
  }
)

type Props = {
  isOpen: boolean
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  lessonNumber?: number | string
  lessonTitle: string
  teacherCalendarId: number
  batchId: string
  classroomId: number | string
  classStartTime: string
  studentId: number
  relationshipManager: RelationshipManagerType
  studentDetails?: StudentProps
}

type RescheduleRange = {
  min: Dayjs
  max: Dayjs
}

/**
 * UI states:
 * 1. Loading
 * 2. Selction
 * 3. Slot selection
 * 4. No slots available
 * 5. Confirm schedule to next class
 * 6. Slot booked success
 * 7. Contact RM
 * 8. Check if student is informed (only for teachers)
 * 9: Request teacher to inform student (only for teachers)
 *
 * Flow chart: https://app.diagrams.net/#G1uW3ssVYBWgPMwbDTrK9CS7phU4NuHCDu
 */
const RescheduleClassModal = ({
  isOpen,
  setIsOpen,
  lessonNumber,
  lessonTitle,
  teacherCalendarId,
  batchId,
  classroomId,
  studentId,
  relationshipManager,
  studentDetails,
  classStartTime,
}: Props) => {
  const { timezone, teacher } = useSessionContext()

  const isTeacher = useMemo(() => !!teacher, [])

  const [selectedTimezone, setSelectedTimezone] = useState(
    timezone || SYSTEM_TIMEZONE
  )
  const [activeScreen, setActiveScreen] = useState<RescheduleModalStatesEnum>(
    RescheduleModalStatesEnum.loading
  )
  const [selectedRescheduleOption, setSelectedRescheduleOption] = useState<
    ClassRescheduleOptionsEnum
  >(null)
  const [rescheduleRequestedBy, setRescheduleRequestedBy] = useState<
    'student' | 'teacher' | null
  >(null)
  const [nextClassTimestamp, setNextClassTimestamp] = useState<string>(null)
  const [upcomingClassesSchedule, setUpcomingClassesSchedule] = useState<
    ClassScheduleType[]
  >([])
  const [isLoading, setIsLoading] = useState(false)
  const [availableSlots, setAvailableSlots] = useState(null)
  const [selectedDate, setSelectedDate] = useState<string>(null)
  const [selectedTime, setSelectedTime] = useState<string>(null)
  const [
    inlineScheduleBeforeNextTimeError,
    setInlineScheduleBeforeNextTimeError,
  ] = useState<string>('')

  const [allowedRescheduleRange, setAllowedRescheduleRange] = useState<
    RescheduleRange
  >({ min: null, max: null })

  const [
    nextClassTimestampInBatchSchedule,
    setNextClassTimestampInBatchSchedule,
  ] = useState<string>(null)

  const handleRescheduleOptionSubmission = () =>
    selectedRescheduleOption === ClassRescheduleOptionsEnum.nextTime
      ? setActiveScreen(
          RescheduleModalStatesEnum.confirmRescheduleAtNextClassTime
        )
      : isTeacher
      ? getAllowedRescheduleRange()
      : getAvailableSlots()

  const handleRescheduleToNextClassTime = async () => {
    if (isLoading) return
    setIsLoading(true)
    try {
      const response = await CClient(`/api/v1/batches/${batchId}/reschedule/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ classroomId, rescheduleRequestedBy }),
      })
      if (!response.ok)
        throw new Error(await flattenErrorsDictFromResponse(response))

      const data: { taskId: string } = await response.json()
      setActiveScreen(RescheduleModalStatesEnum.polling)
      const isPollingCompletedSuccessfully = await pollCeleryTaskStatus(
        data.taskId
      )
      if (!isPollingCompletedSuccessfully) {
        setActiveScreen(
          RescheduleModalStatesEnum.confirmRescheduleAtNextClassTime
        )
        throw new Error(`Polling for celery task id ${data.taskId} failed`)
      }
      setActiveScreen(RescheduleModalStatesEnum.success)
    } catch (error) {
      captureException(error)
      showErrorAlert({
        text:
          'Something went wrong while rescheduling your class. Please refresh the page and try again!',
      })
    }
    setIsLoading(false)
  }

  const getNextClassDetails = async () => {
    setActiveScreen(RescheduleModalStatesEnum.loading)
    try {
      const response = await CClient(
        `/api/v1/classrooms/?state=Upcoming&is_cancelled=False&order_by=Start&limit=1&student_id=${studentId}&after_timestamp=${classStartTime}&classroom_id=${classroomId}`
      )
      if (!response.ok)
        throw new Error(await flattenErrorsDictFromResponse(response))

      const data: ClassDetailsProps[] = await response.json()
      if (data.length === 0) {
        isTeacher
          ? await getAllowedRescheduleRange()
          : await getAvailableSlots()
        return
      }
      setNextClassTimestamp(data[0].start)

      // Fetch batch details to get the schedule
      // If the next class is not in the schedule, then set the next class time as next slot in batch schedule
      const batch_id = data[0].batch.id
      const resp = await CClient(`/api/v1/batches/${batch_id}/`)
      if (!resp.ok) throw new Error(await flattenErrorsDictFromResponse(resp))
      const batch_data = await resp.json()
      const next_class_start_date_in_ist = date(data[0].start).tz(
        'Asia/Kolkata'
      )
      const weekday_of_next_class = next_class_start_date_in_ist
        .format('ddd')
        .toLowerCase()
      const slots = batch_data?.schedule?.slots
      slots && Object.keys(slots).length && slots[weekday_of_next_class]
        ? setNextClassTimestampInBatchSchedule(data[0].start)
        : setNextClassTimestampInBatchSchedule(
            getNextClassDate(classStartTime, slots)
          )

      setActiveScreen(RescheduleModalStatesEnum.rescheduleOptionSelection)
    } catch (error) {
      captureException(error)
      showErrorAlert({
        text:
          'Something went wrong while fetching next class details. Please try again!',
      })
    }
  }

  /** gets previous class's details if previous class is available */
  const getPreviousClassDetails = async () => {
    try {
      setActiveScreen(RescheduleModalStatesEnum.loading)
      const response = await CClient(
        `/api/v1/classrooms/?state=Upcoming&is_cancelled=False&order_by=End&student_id=${studentId}&before_timestamp=${classStartTime}&is_trial=False&limit=1&classroom_id=${classroomId}`
      )

      if (!response.ok)
        throw new Error(await flattenErrorsDictFromResponse(response))

      const data: ClassDetailsProps[] = await response.json()
      return data.length === 0 ? null : data[0]
    } catch (error) {
      captureException(error)
      showErrorAlert({
        text:
          'Something went wrong while fetching previous class details. Please try again!',
      })
    }
  }

  const getAvailableSlots = async () => {
    if (isLoading) return
    setIsLoading(true)

    const previousClassDetails = await getPreviousClassDetails()
    const previousClassTimestamp = previousClassDetails
      ? previousClassDetails.start
      : date().toISOString()

    try {
      // `previousClassTimestamp` is current time if the class does not exists
      const start = date(previousClassTimestamp)
        .add(15, 'minutes')
        .toISOString()
      // If current class is last class OR next class is after 7+ days then set
      // end to be today + 7 days else set next class's start
      const end =
        !nextClassTimestamp ||
        date(nextClassTimestamp)
          .startOf('day')
          .diff(date().startOf('day'), 'days') > 7
          ? date(start).startOf('day').add(7, 'days').toISOString()
          : date(nextClassTimestamp).startOf('day').toISOString()

      const response = await CClient(
        `/api/v1/calendars/${teacherCalendarId}/slots/?start=${start}&end=${end}&event_type=Available slot&is_cancelled=false&is_expandable=true`
      )
      if (!response.ok)
        throw new Error(await flattenErrorsDictFromResponse(response))

      const data = await response.json()

      if (data.length === 0) {
        setActiveScreen(
          nextClassTimestamp
            ? RescheduleModalStatesEnum.noSlotsAvailable
            : RescheduleModalStatesEnum.contactRM
        )
        setIsLoading(false)
        return
      }

      const slots = {}
      data.forEach((slot: any) => {
        const startDate = date(slot.start)
          .tz(selectedTimezone)
          .format('DD-MM-YYYY')

        // eslint-disable-next-line no-prototype-builtins
        if (!slots.hasOwnProperty(startDate)) {
          slots[startDate] = []
        }
        slots[startDate].push(date(slot.start).tz(selectedTimezone).format())
      })

      setSelectedDate(Object.keys(slots)[0])
      setAvailableSlots(slots)
      setActiveScreen(RescheduleModalStatesEnum.availableSlots)
    } catch (error) {
      captureException(error)
      showErrorAlert({
        text:
          'Something went wrong while fetching available slots. Please try again!',
      })
    }

    setIsLoading(false)
  }

  const handleReschdeuleBeforeNextClass = async () => {
    const onSuccess = () => {
      setActiveScreen(RescheduleModalStatesEnum.success)
      if (teacher)
        setUpcomingClassesSchedule([
          {
            lessonNumber,
            lessonTitle,
            startTime: selectedTime,
            studentName: studentDetails.name,
          },
        ])
    }
    const on404 = () => {
      setSelectedTime(null)
      isTeacher ? getAllowedRescheduleRange() : getAvailableSlots()
      showErrorAlert({
        text:
          'Sorry! The time slot you selected is no longer available. Please select another slot.',
      })
    }
    handleReschdeuleAtSelectedTime({
      classroomId,
      isLoading,
      selectedDate,
      selectedTime,
      setInlineError: setInlineScheduleBeforeNextTimeError,
      setIsLoading,
      onSuccess,
      isStudent: rescheduleRequestedBy
        ? rescheduleRequestedBy === 'student'
        : true,
      on404,
      isTeacher: isTeacher,
    })
  }

  const getUpcomingClassesSchedule = async () => {
    setIsLoading(true)
    try {
      const response = await CClient(
        `/api/v1/classrooms/?state=Upcoming&is_cancelled=False&order_by=Start&limit=3&student_id=${studentId}&classroom_id=${classroomId}`
      )
      if (!response.ok)
        throw new Error(`${response.status}: ${response.statusText}`)
      const data: ClassDetailsProps[] = await response.json()
      setUpcomingClassesSchedule(
        data.map(({ start, lesson, lessonNumber, batch }) => ({
          lessonNumber,
          lessonTitle: lesson?.title,
          startTime: start,
          studentName: batch?.students?.[0]?.name,
        }))
      )
    } catch (error) {
      captureException(error)
      showErrorAlert(
        {
          text:
            'The class is rescheduled but something went wrong while fetching upcoming classes details.',
        },
        () => Router.reload()
      )
    }
    setIsLoading(false)
  }

  const getAllowedRescheduleRange = async () => {
    const previousClassDetails = await getPreviousClassDetails()

    // if current time is 2:26 PM, then add 10 mins to make it 2:36 PM
    // then add 15 - (36 % 15) = 15 - 6 = 9 mins to make it 2:45 PM
    const now = date().tz(selectedTimezone).add(10, 'minutes')
    const minutes = now.minute()
    const minutesToAdd = 15 - (minutes % 15)
    const roundedTime = now.add(minutesToAdd, 'minutes')

    const start = previousClassDetails
      ? date(previousClassDetails.start).tz(selectedTimezone).add(1, 'hours')
      : roundedTime
    // If current class is last class OR next class is after 7+ days then set
    // end to be today + 7 days else set next class's start
    const end = !nextClassTimestamp
      ? start.startOf('day').add(7, 'days')
      : date(nextClassTimestamp).tz(selectedTimezone).subtract(15)

    setAllowedRescheduleRange({ min: start, max: end })
    setSelectedDate(start.format('DD-MM-YYYY'))
    setSelectedTime(start.format())
    setActiveScreen(RescheduleModalStatesEnum.selectRescheduleDateTime)
  }

  useEffect(() => {
    if (activeScreen === RescheduleModalStatesEnum.success)
      getUpcomingClassesSchedule()
  }, [activeScreen])

  useEffect(() => {
    if (isOpen) {
      teacher
        ? setActiveScreen(RescheduleModalStatesEnum.checkIfStudentIsInformed)
        : getNextClassDetails()
    } else {
      setActiveScreen(RescheduleModalStatesEnum.loading)
      setSelectedRescheduleOption(null)
      setSelectedDate(null)
      setSelectedTime(null)
      setRescheduleRequestedBy(null)
    }
  }, [isOpen])

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      shouldCloseOnEsc
      width={
        activeScreen === RescheduleModalStatesEnum.availableSlots
          ? 'max-w-2xl'
          : activeScreen === RescheduleModalStatesEnum.success && isTeacher
          ? 'max-w-md'
          : 'max-w-lg'
      }
    >
      <>
        <div className="relative flex justify-end">
          <X
            className="absolute top-0 right-0 w-5 -mt-5 -mr-5 cursor-pointer text-grey-800"
            onClick={() => setIsOpen(false)}
          />
        </div>
        <div
          className="overflow-auto thin-scroll-bar"
          style={{ maxHeight: '80vh' }}
        >
          {activeScreen === RescheduleModalStatesEnum.loading && (
            <div className="flex items-center justify-center h-40">
              <Spinner classes="text-orange h-10 w-10" />
            </div>
          )}
          {activeScreen ===
            RescheduleModalStatesEnum.checkIfStudentIsInformed && (
            <CheckIfStudentIsInformed
              onNoClick={() =>
                setActiveScreen(
                  RescheduleModalStatesEnum.requestTeacherToInformStudent
                )
              }
              onYesClick={() => getNextClassDetails()}
              start={classStartTime}
              lessonNumber={lessonNumber}
              lessonTitle={lessonTitle}
              rescheduleRequestedBy={rescheduleRequestedBy}
              setRescheduleRequestedBy={setRescheduleRequestedBy}
            />
          )}
          {activeScreen ===
            RescheduleModalStatesEnum.requestTeacherToInformStudent && (
            <RequestTeacherToInformStudent
              onOkClick={() => setIsOpen(false)}
              start={classStartTime}
              studentDetails={studentDetails}
              lessonNumber={lessonNumber}
              lessonTitle={lessonTitle}
            />
          )}
          {activeScreen ===
            RescheduleModalStatesEnum.rescheduleOptionSelection && (
            <Selection
              rescheduleRequestedBy={rescheduleRequestedBy}
              selectedRescheduleOption={selectedRescheduleOption}
              setSelectedRescheduleOption={setSelectedRescheduleOption}
              lessonNumber={lessonNumber}
              lessonTitle={lessonTitle}
              onContinue={handleRescheduleOptionSubmission}
              onGoBack={() => setIsOpen(false)}
              timezone={selectedTimezone}
              isLoading={isLoading}
              isTeacher={isTeacher}
              nextClassTimestampInBatchSchedule={
                nextClassTimestampInBatchSchedule
              }
            />
          )}
          {activeScreen ===
            RescheduleModalStatesEnum.confirmRescheduleAtNextClassTime && (
            <ConfirmNextClassTime
              lessonNumber={lessonNumber}
              lessonTitle={lessonTitle}
              onConfirm={handleRescheduleToNextClassTime}
              onGoBack={() =>
                setActiveScreen(
                  RescheduleModalStatesEnum.rescheduleOptionSelection
                )
              }
              timezone={selectedTimezone}
              isLoading={isLoading}
              isTeacher={isTeacher}
              rescheduleRequestedBy={rescheduleRequestedBy}
              nextClassTimestampInBatchSchedule={
                nextClassTimestampInBatchSchedule
              }
            />
          )}
          {activeScreen === RescheduleModalStatesEnum.polling && <Polling />}
          {activeScreen === RescheduleModalStatesEnum.availableSlots && (
            <AvailableSlots
              isBooking={isLoading}
              selectedDate={selectedDate}
              selectedTime={selectedTime}
              selectedTimezone={selectedTimezone}
              setSelectedDate={setSelectedDate}
              setSelectedTime={setSelectedTime}
              setSelectedTimezone={setSelectedTimezone}
              slots={availableSlots}
              onBook={handleReschdeuleBeforeNextClass}
              title={`You are rescheduling Lesson ${lessonNumber}: ${lessonTitle}`}
              timezoneAlertBgColor="yellow"
              onGoBack={
                nextClassTimestamp
                  ? () =>
                      setActiveScreen(
                        RescheduleModalStatesEnum.rescheduleOptionSelection
                      )
                  : null
              }
              ctaText="Confirm rescheduling"
              error={inlineScheduleBeforeNextTimeError}
            />
          )}
          {activeScreen ===
            RescheduleModalStatesEnum.selectRescheduleDateTime && (
            <SelectRescheduleDateTime
              selectedTimezone={selectedTimezone}
              selectedTime={selectedTime}
              setSelectedTimezone={setSelectedTimezone}
              setSelectedDate={setSelectedDate}
              setSelectedTime={setSelectedTime}
              title={`You are rescheduling Lesson: ${lessonTitle}`}
              isBooking={isLoading}
              onConfirm={handleReschdeuleBeforeNextClass}
              allowedRescheduleRange={allowedRescheduleRange}
              onGoBack={
                nextClassTimestamp
                  ? () =>
                      setActiveScreen(
                        RescheduleModalStatesEnum.rescheduleOptionSelection
                      )
                  : () => setIsOpen(false)
              }
            />
          )}
          {activeScreen === RescheduleModalStatesEnum.contactRM && (
            <ContactRm
              lessonNumber={lessonNumber}
              lessonTitle={lessonTitle}
              onGoBack={() => setIsOpen(false)}
              relationshipManager={relationshipManager}
              isTeacher={isTeacher}
            />
          )}
          {activeScreen === RescheduleModalStatesEnum.noSlotsAvailable && (
            <NoSlotsAvailable
              lessonNumber={lessonNumber}
              lessonTitle={lessonTitle}
              onAttendClassNextTime={() =>
                setActiveScreen(
                  RescheduleModalStatesEnum.confirmRescheduleAtNextClassTime
                )
              }
              onGoBack={() => setIsOpen(false)}
              selectedTimezone={selectedTimezone}
              nextClassTimestamp={nextClassTimestamp}
              isTeacher={isTeacher}
            />
          )}
          {activeScreen === RescheduleModalStatesEnum.success && (
            <RescheduleSuccess
              scheduleOfUpcomingClasses={upcomingClassesSchedule}
              isLoading={isLoading}
              isTeacher={isTeacher}
              selectedTimezone={selectedTimezone}
              rescheduleRequestedBy={rescheduleRequestedBy}
            />
          )}
        </div>
      </>
    </Modal>
  )
}

export default RescheduleClassModal
